import { Seo } from "@/seo/Seo";
import { Section } from "@/components/common/Section";
import { Container } from "@/components/common/Container";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { DetailHero } from "@/components/detail/DetailHero";
import { PillList } from "@/components/detail/PillList";
import { FeatureGrid, BenefitGrid } from "@/components/detail/FeatureGrid";
import { WorkflowTimeline } from "@/components/detail/WorkflowTimeline";
import { ServiceComparisonTable } from "@/components/detail/ComparisonTable";
import { FaqAccordion } from "@/components/detail/FaqAccordion";
import { CaseStudyPreview } from "@/components/detail/CaseStudyPreview";
import { RelatedLinks, type RelatedLink } from "@/components/detail/RelatedLinks";
import { DetailCta } from "@/components/detail/DetailCta";
import { serviceDetails } from "@/data/serviceDetails";
import { industries } from "@/data/industries.data";
import type { SolutionDetail } from "@/types/detail";
import type { ServiceDetail } from "@/types/detail";

function relatedServiceLinks(slugs: string[]): RelatedLink[] {
  return slugs
    .map((slug) => serviceDetails.find((s) => s.slug === slug))
    .filter((s): s is ServiceDetail => Boolean(s))
    .map((s) => ({ label: s.title, description: s.category, to: `/services/${s.slug}`, icon: s.icon }));
}

function relatedIndustryLinks(ids: string[]): RelatedLink[] {
  return ids
    .map((id) => industries.find((i) => i.id === id))
    .filter((i): i is (typeof industries)[number] => Boolean(i))
    .map((i) => ({ label: i.name, to: "/industries", icon: i.icon }));
}

export function SolutionDetailTemplate({ data }: { data: SolutionDetail }) {
  const words = data.title.split(" ");
  const titleLines =
    words.length > 2 ? [words.slice(0, Math.ceil(words.length / 2)).join(" "), words.slice(Math.ceil(words.length / 2)).join(" ")] : [data.title];

  return (
    <>
      <Seo title={data.title} description={data.tagline} path={`/solutions/${data.slug}`} />
      <DetailHero
        category={`Solutions · ${data.domain}`}
        titleLines={titleLines}
        tagline={data.tagline}
        icon={data.icon}
        accent={data.accent}
        image={data.image}  //added by sudip
      />

      {/* Overview + who should use */}
      <Section spacing="compact">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[1.3fr_0.7fr] lg:gap-16">
            <div>
              <SectionHeading eyebrow="Overview" title="Business overview" />
              <Reveal>
                <p className="mt-6 text-lg leading-relaxed text-muted-foreground">{data.overview}</p>
              </Reveal>
            </div>
            <div className="lg:pt-4">
              <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Who should use it
              </h3>
              <div className="mt-5">
                <PillList items={data.whoShouldUse} columns={1} />
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Challenges */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <SectionHeading
            eyebrow="Common challenges"
            title="The problems this solves"
            description="The everyday frustrations this replaces with clarity and control."
          />
          <div className="mt-10">
            <PillList items={data.challenges} />
          </div>
        </Container>
      </Section>

      {/* Modules / features */}
      <Section spacing="compact">
        <Container>
          <SectionHeading
            eyebrow="Key modules"
            title="What's inside — and why it matters"
            description="Every module is here to serve a clear business outcome."
          />
          <div className="mt-12">
            <FeatureGrid features={data.modules} />
          </div>
        </Container>
      </Section>

      {/* Benefits */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <SectionHeading eyebrow="Benefits" title="The value it delivers" />
          <div className="mt-10">
            <BenefitGrid benefits={data.benefits} />
          </div>
        </Container>
      </Section>

      {/* Example workflow */}
      <Section spacing="compact">
        <Container>
          <SectionHeading
            eyebrow="How it works"
            title="An example workflow"
            description="Click any step to see what happens at each stage."
          />
          <div className="mt-10">
            <WorkflowTimeline nodes={data.workflow} accent={data.accent} />
          </div>
        </Container>
      </Section>

      {/* Comparison */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <SectionHeading
            eyebrow="Why custom"
            title="Spreadsheet vs generic vs custom"
            description="Where a purpose-built VibeWell solution pulls ahead."
          />
          <div className="mt-10">
            <ServiceComparisonTable rows={data.comparison} />
          </div>
        </Container>
      </Section>

      {/* Integrations + typical users */}
      <Section spacing="compact">
        <Container>
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <SectionHeading eyebrow="Connects with" title="Suggested integrations" />
              <div className="mt-8">
                <PillList items={data.integrations} variant="chip" />
              </div>
            </div>
            <div>
              <SectionHeading eyebrow="Made for" title="Typical users" />
              <div className="mt-8">
                <PillList items={data.typicalUsers} variant="chip" />
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* FAQ */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <SectionHeading eyebrow="FAQ" title="Frequently asked questions" align="center" />
          <div className="mt-10">
            <FaqAccordion faqs={data.faqs} />
          </div>
        </Container>
      </Section>

      {/* Case study */}
      <Section spacing="compact">
        <Container>
          <SectionHeading
            eyebrow="Concept work"
            title="How this comes to life"
            description="A concept project illustrating the kind of outcome this solution delivers."
          />
          <div className="mt-10">
            <CaseStudyPreview data={data.caseConcept} />
          </div>
        </Container>
      </Section>

      {/* Related */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <SectionHeading eyebrow="Keep exploring" title="Related services" />
          <div className="mt-8">
            <RelatedLinks items={relatedServiceLinks(data.relatedServices)} />
          </div>
          <h3 className="mt-14 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Related industries
          </h3>
          <div className="mt-6">
            <RelatedLinks items={relatedIndustryLinks(data.relatedIndustries)} />
          </div>
        </Container>
      </Section>

      <DetailCta origin={{ type: "solution", slug: data.slug, label: data.title }} />
    </>
  );
}
